import type { JSX } from 'react';
import { Link } from 'react-router-dom';

/**
 * Политика обработки данных.
 *
 * Обязательна по закону для сервиса, который собирает почту и имена. Но пишется
 * не для проверяющего, а для человека: сплошная юридическая калька из шаблона
 * не сообщает читателю ничего и своей цели — объяснить, что происходит
 * с его данными, — не достигает.
 *
 * Правило текста: здесь описано ровно то, что делает код. Ни одного обещания,
 * которого нет в продукте.
 */

const ОБНОВЛЕНО = '23 июля 2026 года';

/*
 * Адрес для обращений.
 *
 * Задаётся при сборке (VITE_CONTACT_EMAIL) и намеренно не имеет значения
 * по умолчанию: выдуманный адрес в правилах хуже отсутствующего — человек
 * напишет в пустоту и решит, что его игнорируют.
 */
const КОНТАКТ = (import.meta.env.VITE_CONTACT_EMAIL ?? '').trim();

export function PrivacyPage(): JSX.Element {
  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to="/" className="topbar__brand">
          <span className="topbar__mark" aria-hidden="true">
            ✦
          </span>
          Загадарь
        </Link>
      </header>

      <main className="app-main">
        <article className="prose">
          <h1>Что мы знаем о вас</h1>
          <p className="muted">Обновлено {ОБНОВЛЕНО}</p>

          <h2>Коротко</h2>
          <p>
            Мы храним почту, имя и то, что вы сами внесли в списки. Мы не продаём данные,
            не показываем рекламу и не передаём ничего третьим лицам. Аккаунт можно удалить
            в любой момент из настроек, и это удаление настоящее — вместе со всеми списками,
            фотографиями и бронями.
          </p>

          <h2>Что именно хранится</h2>
          <ul>
            <li>
              <strong>Почта</strong> — чтобы вы могли войти и восстановить доступ. Больше
              ни для чего: писем-рассылок мы не шлём.
            </li>
            <li>
              <strong>Пароль</strong> — в виде необратимого отпечатка (argon2). Сам пароль
              не хранится нигде, и восстановить его из отпечатка нельзя даже нам.
            </li>
            <li>
              <strong>Имя, имя пользователя, пара слов о себе, день рождения, фотография</strong>{' '}
              — если вы их указали. Всё это необязательно, кроме имени.
            </li>
            <li>
              <strong>Списки и желания</strong> — названия, описания, ссылки, цены и картинки,
              которые вы добавили.
            </li>
            <li>
              <strong>Брони</strong> — кто какой подарок взял. Эти записи владельцу списка
              не показываются никогда: см. ниже.
            </li>
            <li>
              <strong>Сессии</strong> — отпечаток адреса и строка браузера, чтобы вы могли
              видеть, откуда входили, и завершить чужие сеансы. Адрес хранится не целиком,
              а в виде отпечатка.
            </li>
          </ul>

          <h2>Про тайну броней</h2>
          <p>
            Владелец списка не видит, кто и что забронировал. Это не настройка, которую можно
            случайно выключить: запрос к таблице броней для владельца просто не выполняется,
            а бронирование не меняет ни одного поля в самом желании — иначе владелец
            вычислил бы бронь по времени изменения.
          </p>

          <h2>Куда уходят данные</h2>
          <p>
            Никуда. Сервис работает на одном сервере в России, аналитики и внешних счётчиков
            в нём нет. Единственный внешний запрос делается тогда, когда вы вставляете ссылку:
            сервер открывает эту страницу, чтобы взять с неё название, цену и фотографию.
            Магазин при этом видит обращение с нашего сервера, а не с вашего устройства —
            то есть о вас он не узнаёт ничего.
          </p>
          <p>
            Письмо приходит только одно и только по вашей просьбе — со ссылкой для смены
            пароля.
          </p>

          <h2>Cookie</h2>
          <p>
            Одна: та, что помнит ваш вход. Она недоступна скриптам страницы и живёт 90 дней.
            Рекламных и следящих cookie нет.
          </p>

          <h2>Сколько это хранится</h2>
          <p>
            Пока существует аккаунт. Удалите аккаунт — и записи удаляются сразу, а не
            «помечаются удалёнными». Резервные копии базы хранятся 14 дней и затирают друг
            друга по кругу; дольше этого срока ваши данные не живут нигде.
          </p>

          <h2>Ваши права</h2>
          <p>
            Изменить или удалить свои данные можно самостоятельно в настройках, без писем
            и заявлений: имя, фотография и всё содержимое списков правятся там же, где
            заводились, а кнопка «Удалить аккаунт» удаляет всё разом и сразу.
          </p>
          {КОНТАКТ && (
            <p>
              Если что-то не получается или остались вопросы — напишите на{' '}
              <a href={`mailto:${КОНТАКТ}`}>{КОНТАКТ}</a>.
            </p>
          )}

          <h2>Изменения</h2>
          <p>
            Если правила изменятся, здесь появится новая дата. Мы не станем менять их так,
            чтобы уже собранные данные начали использоваться иначе, чем сказано сейчас.
          </p>

          <p>
            <Link to="/" className="text-link">
              Вернуться
            </Link>
          </p>
        </article>
      </main>
    </div>
  );
}
